import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../formStyles.css';
import {
  fetchClientByCode,
  createClient,
  updateClient,
  deleteClient,
  duplicateClient,
} from '../../services/api';
import MessageBox from '../MessageBox/MessageBox';
import MailBook from '../MailBook/MailBook';
import PostcodeCityInputs from '../PostcodeCityInputs/PostcodeCityInputs';
import IconButton from '../IconButton/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useForm } from '../BaseForm/FormContext';
import { defaultClientData } from '../../dataSets/defaultFormData';
import { useEstablishmentSync } from '../../hooks/useEstablishmentSync';
import { formatDateStringToFrenchDate, formatDateTimeToFrenchDate } from '../../utils/dateUtils';

export default function ClientForm() {
  const {
    isLoading,
    setIsLoading,
    isNew,
    setIsNew,
    formData,
    setFormData,
    messageBoxConfig,
    setMessageBoxConfig,
    showMessageBox,
    setShowMessageBox,
    handleChange,
    handleBack,
    handleConfirmBack,
    handleFakeFormClick,
    setHasChanged,
  } = useForm();
  const { client_code } = useParams();
  const { handleChangeWithSync } = useEstablishmentSync(
    formData,
    setFormData,
    handleChange,
    'client',
    setHasChanged,
    isLoading
  );

  const [showMailBook, setShowMailBook] = useState(false);
  const [isPrefillHidden, setIsPrefillHidden] = useState(false);

  useEffect(() => {
    const fetchData = async client_code => {
      if (client_code && client_code !== 'nouveau') {
        try {
          const fetchedData = await fetchClientByCode(client_code);
          const updatedFormData = {};
          Object.keys(formData).forEach(key => {
            updatedFormData[key] =
              fetchedData[key] !== undefined && fetchedData[key] !== null
                ? fetchedData[key]
                : formData[key];
          });
          setFormData(updatedFormData);
          setIsLoading(false);
        } catch (error) {
          console.error('Erreur lors de la récupération du client:', error);
        }
      } else if (client_code && client_code === 'nouveau') {
        setFormData(defaultClientData);
        setIsLoading(false);
      }
    };

    setIsNew(client_code === 'nouveau');
    fetchData(client_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client_code]);

  const validateFields = () => {
    let isValid = true;
    let newInvalidFields = [];

    if (formData.diesel_tax < 0 || formData.diesel_tax > 100) {
      isValid = false;
      newInvalidFields.push('Taxe gasoil: doit être comprise entre 0 et 100.');
    }

    if (formData.payment_time_from_billing < 0) {
      isValid = false;
      newInvalidFields.push("Délai d'échéance: doit être un nombre entier positif.");
    }

    if (newInvalidFields.length > 0) {
      setMessageBoxConfig({
        message: `Une ou plusieurs erreurs ont été détectées: ${newInvalidFields.join('\n')}`,
        type: 'ok',
        loading_button: false,
        input: false,
        onConfirm: () => setShowMessageBox(false),
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }

    return isValid;
  };

  const handleBlurName = () => {
    let updatedData = {
      ...formData,
    };
    if (!formData.pickup_name && !formData.billing_name) {
      updatedData = {
        ...updatedData,
        pickup_name: formData.name,
        billing_name: formData.name,
      };
    }
    setFormData(updatedData);
  };

  const handleBlurDieselTax = () => {
    if (formData.diesel_tax < 1) {
      let updatedData = {
        ...formData,
        diesel_tax: formData.diesel_tax * 100,
      };
      setFormData(updatedData);
    }
  };

  const togglePrefill = () => {
    setIsPrefillHidden(!isPrefillHidden);
  };

  const handleShowMailbook = () => {
    setShowMailBook(true);
  };

  const handleCreateClient = e => {
    handleSubmit(e, formData);
  };

  const handleModifyClient = e => {
    handleSubmit(e, formData);
  };

  const handleDeleteClient = async () => {
    const DeleteClient = async () => {
      try {
        const response = await deleteClient(formData);
        if (response.status === 204) {
          setMessageBoxConfig({
            message: `Le client ${client_code} a été supprimé définitivement.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => {
              setShowMessageBox(false);
              handleBack();
            },
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        } else {
          setMessageBoxConfig({
            message: `La suppression du client ${client_code} a rencontré un comportement innatendu: code ${response.status}.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => setShowMessageBox(false),
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      } catch (error) {
        setMessageBoxConfig({
          message: `La suppression du client ${client_code} a rencontré une erreur. La suppression est impossible si un ou plusieurs dossiers existent pour ce client.`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => setShowMessageBox(false),
          onCancel: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    };

    setMessageBoxConfig({
      message: `Etes vous certain.e de vouloir supprimer le client ${client_code} ?`,
      type: 'yesno',
      loading_button: false,
      input: false,
      onConfirm: () => {
        setShowMessageBox(false);
        DeleteClient();
      },
      onCancel: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);
  };

  const handleDuplicateClient = async () => {
    setMessageBoxConfig({
      message: 'Entrez le nouveau code client:',
      type: 'input',
      loading_button: false,
      input: true,
      onConfirm: async newClientCode => {
        if (!newClientCode || newClientCode.trim() === '') {
          setMessageBoxConfig({
            message: 'Le code client ne peut pas être vide.',
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => setShowMessageBox(false),
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
          return;
        }

        setShowMessageBox(false);

        // Attendre un court délai pour que la MessageBox se démonte et remonte
        await new Promise(resolve => setTimeout(resolve, 100));

        setMessageBoxConfig({
          message: 'Entrez le nouveau nom du client:',
          type: 'input',
          loading_button: false,
          input: true,
          onConfirm: async newName => {
            if (!newName || newName.trim() === '') {
              setMessageBoxConfig({
                message: 'Le nom du client ne peut pas être vide.',
                type: 'ok',
                loading_button: false,
                input: false,
                onConfirm: () => setShowMessageBox(false),
                onCancel: () => setShowMessageBox(false),
              });
              setShowMessageBox(true);
              return;
            }

            setMessageBoxConfig({
              message: `Duplication du client ${client_code} en cours...`,
              type: 'loading',
              loading_button: true,
              input: false,
            });
            setShowMessageBox(true);

            try {
              const response = await duplicateClient(client_code, newClientCode, newName);
              if (response.status === 201) {
                setMessageBoxConfig({
                  message: `Le client ${client_code} a été dupliqué avec succès. Nouveau code: ${newClientCode}`,
                  type: 'ok',
                  loading_button: false,
                  input: false,
                  onConfirm: () => {
                    setShowMessageBox(false);
                    window.location.href = `/clients/${newClientCode}`;
                  },
                  onCancel: () => setShowMessageBox(false),
                });
                setShowMessageBox(true);
              } else {
                setMessageBoxConfig({
                  message: `La duplication du client ${client_code} a rencontré un comportement inattendu: code ${response.status}.`,
                  type: 'ok',
                  loading_button: false,
                  input: false,
                  onConfirm: () => setShowMessageBox(false),
                  onCancel: () => setShowMessageBox(false),
                });
                setShowMessageBox(true);
              }
            } catch (error) {
              const errorMessage =
                error.response?.data?.error ||
                `La duplication du client ${client_code} a rencontré une erreur.`;
              setMessageBoxConfig({
                message: errorMessage,
                type: 'ok',
                loading_button: false,
                input: false,
                onConfirm: () => setShowMessageBox(false),
                onCancel: () => setShowMessageBox(false),
              });
              setShowMessageBox(true);
            }
          },
          onCancel: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      },
      onCancel: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);
  };

  const handleSubmit = async (e, updatedData) => {
    e.preventDefault();

    try {
      if (!validateFields()) {
        return;
      }

      // TRAITEMENT DIESEL TAX
      if (updatedData.diesel_tax < 1) {
        const decimal_tax = updatedData.diesel_tax;
        updatedData = {
          ...updatedData,
          diesel_tax: decimal_tax * 100,
        };
      }

      if (isNew) {
        const response = await createClient(updatedData);
        if (response.status === 201) {
          setMessageBoxConfig({
            message: `Le client ${formData.client_code} a bien été créé.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => {
              setHasChanged(false);
              setShowMessageBox(false);
              handleConfirmBack();
            },
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      } else {
        const response = await updateClient(updatedData);
        if (response.status === 200) {
          setMessageBoxConfig({
            message: `Le client ${formData.client_code} a bien été mise à jour.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => {
              setHasChanged(false);
              setShowMessageBox(false);
              handleConfirmBack();
            },
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      }
    } catch (error) {
      setMessageBoxConfig({
        message: `Erreur lors de la sauvegarde du client: ${error}.`,
        type: 'ok',
        loading_button: false,
        input: false,
        onConfirm: () => setShowMessageBox(false),
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }
  };

  const handleCloseMailBook = () => {
    setShowMailBook(false);
  };

  const handleConfirmMailBook = async ({ email_book, default_emails }) => {
    // Calculer le billing_email (premier email du carnet)
    const newBillingEmail = email_book.split(',')[0]?.trim() || '';

    if (
      email_book !== formData.email_book ||
      JSON.stringify(default_emails) !== JSON.stringify(formData.default_emails || [])
    ) {
      const updatedFormData = {
        ...formData,
        email_book,
        default_emails,
        billing_email: newBillingEmail,
      };

      if (isNew) {
        // Pour un nouveau client, on met à jour seulement le formData local
        // Les emails seront sauvegardés lors de la création du client
        setFormData(updatedFormData);
        setHasChanged(true);
        setShowMailBook(false);
      } else {
        // Pour un client existant, on sauvegarde immédiatement
        try {
          const response = await updateClient(updatedFormData);
          if (response.status === 200) {
            setFormData(updatedFormData);
            setShowMailBook(false);
          }
        } catch (error) {
          setMessageBoxConfig({
            message: `Erreur lors de la sauvegarde du carnet de mails: ${error}.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => setShowMessageBox(false),
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      }
    } else {
      setShowMailBook(false);
    }
  };

  const handleCancelMailBook = () => {
    setShowMailBook(false);
  };

  return (
    <div>
      <div className="form-style">
        <div className="form-header grid-layout">
          <div className="goBackButton">
            <button type="button" onClick={handleBack}>
              Retour
            </button>
          </div>
          <div className="form-title-container">
            {isNew ? <h1>Nouveau client</h1> : <h1>Client {formData.client_code}</h1>}
          </div>
          <div className="form-header-right-container">
            {isNew && process.env.NODE_ENV !== 'production' && (
              <button className="fake-form-fill" type="button" onClick={handleFakeFormClick}>
                Générer un faux client
              </button>
            )}
            {!isNew && (
              <div className="form-header-button-container">
                <IconButton
                  icon={ContentCopyIcon}
                  text=""
                  onClick={handleDuplicateClient}
                  variant="contained"
                  styleClass={'no-background'}
                  title="Dupliquer le client"
                />
              </div>
            )}
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-style-client-name form-div">
            <div className="special-field-container">
              <div className="special-field full form-div">
                <h3>Code client:</h3>
                <input
                  type="text"
                  name="client_code"
                  value={formData.client_code}
                  onChange={handleChange}
                  disabled={!isNew}
                />
              </div>
              {!isNew && (
                <div className="special-field dates form-div">
                  {formData.creation_date && (
                    <p>Créé le {formatDateStringToFrenchDate(formData.creation_date)}</p>
                  )}
                  {formData.updated_at && (
                    <p>Modifié le {formatDateTimeToFrenchDate(formData.updated_at)}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-style-establishment-title form-div">
            <h2>Adresse établissement (siège)</h2>
          </div>

          <div className="form-style-establishment-1 form-div">
            <div className="horizontal-input-group">
              <label>Nom du client :</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChangeWithSync}
                onBlur={handleBlurName}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse :</label>
              <input
                type="text"
                name="establishment_address1"
                value={formData.establishment_address1}
                onChange={handleChangeWithSync}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse 2 :</label>
              <input
                type="text"
                name="establishment_address2"
                value={formData.establishment_address2}
                onChange={handleChangeWithSync}
              />
            </div>
            <PostcodeCityInputs
              prefix="establishment"
              postCodeFieldName="establishment_postcode"
              cityFieldName="establishment_city"
              handleChange={handleChangeWithSync}
              formData={formData}
              setFormData={setFormData}
              loading={isLoading}
            />
            <div className="horizontal-input-group">
              <label>Pays :</label>
              <input
                type="text"
                name="establishment_country"
                value={formData.establishment_country}
                onChange={handleChangeWithSync}
              />
            </div>
          </div>

          <div className="form-style-establishment-2 form-div">
            <div className="horizontal-input-group">
              <label>Contact :</label>
              <input
                type="text"
                name="establishment_contact"
                value={formData.establishment_contact}
                onChange={handleChangeWithSync}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 1 :</label>
              <input
                type="text"
                name="establishment_phone1"
                value={formData.establishment_phone1}
                onChange={handleChangeWithSync}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 2 :</label>
              <input
                type="text"
                name="establishment_phone2"
                value={formData.establishment_phone2}
                onChange={handleChangeWithSync}
              />
            </div>
            {/* <div className="horizontal-input-group">
              <label>Télécopie :</label>
              <input
                type="text"
                name="establishment_fax"
                value={formData.establishment_fax}
                onChange={handleChange}
              />
            </div> */}
            <div className="horizontal-input-group">
              <label>Mail :</label>
              <input
                type="text"
                name="establishment_email"
                value={formData.establishment_email}
                onChange={handleChangeWithSync}
              />
            </div>
          </div>

          <div className="form-style-pickup-title form-div">
            <h2>Adresse enlèvement</h2>
          </div>

          <div className="form-style-pickup-1 form-div">
            <div className="horizontal-input-group">
              <label>Nom du client :</label>
              <input
                type="text"
                name="pickup_name"
                value={formData.pickup_name}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse :</label>
              <input
                type="text"
                name="pickup_address1"
                value={formData.pickup_address1}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse 2 :</label>
              <input
                type="text"
                name="pickup_address2"
                value={formData.pickup_address2}
                onChange={handleChange}
              />
            </div>
            <PostcodeCityInputs
              prefix="pickup"
              postCodeFieldName="pickup_postcode"
              cityFieldName="pickup_city"
              handleChange={handleChange}
              formData={formData}
              setFormData={setFormData}
              loading={isLoading}
            />
            <div className="horizontal-input-group">
              <label>Pays :</label>
              <input
                type="text"
                name="pickup_country"
                value={formData.pickup_country}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-pickup-2 form-div">
            <div className="horizontal-input-group">
              <label>Contact :</label>
              <input
                type="text"
                name="pickup_contact"
                value={formData.pickup_contact}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 1 :</label>
              <input
                type="text"
                name="pickup_phone1"
                value={formData.pickup_phone1}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 2 :</label>
              <input
                type="text"
                name="pickup_phone2"
                value={formData.pickup_phone2}
                onChange={handleChange}
              />
            </div>
            {/* <div className="horizontal-input-group">
              <label>Télécopie :</label>
              <input
                type="text"
                name="pickup_fax"
                value={formData.pickup_fax}
                onChange={handleChange}
              />
            </div> */}
            <div className="horizontal-input-group">
              <label>Mail :</label>
              <input
                type="text"
                name="pickup_email"
                value={formData.pickup_email}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-billing-address-title form-div">
            <h2>Adresse facturation</h2>
          </div>

          <div className="form-style-billing-address-1 form-div">
            <div className="horizontal-input-group">
              <label>Nom du client :</label>
              <input
                type="text"
                name="billing_name"
                value={formData.billing_name}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse :</label>
              <input
                type="text"
                name="billing_address1"
                value={formData.billing_address1}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse 2 :</label>
              <input
                type="text"
                name="billing_address2"
                value={formData.billing_address2}
                onChange={handleChange}
              />
            </div>
            <PostcodeCityInputs
              prefix="billing"
              postCodeFieldName="billing_postcode"
              cityFieldName="billing_city"
              handleChange={handleChange}
              formData={formData}
              setFormData={setFormData}
              loading={isLoading}
            />
            <div className="horizontal-input-group">
              <label>Pays :</label>
              <input
                type="text"
                name="billing_country"
                value={formData.billing_country}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-billing-address-2 form-div">
            <div className="horizontal-input-group">
              <label>Contact :</label>
              <input
                type="text"
                name="billing_contact"
                value={formData.billing_contact}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 1 :</label>
              <input
                type="text"
                name="billing_phone1"
                value={formData.billing_phone1}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 2 :</label>
              <input
                type="text"
                name="billing_phone2"
                value={formData.billing_phone2}
                onChange={handleChange}
              />
            </div>
            {/* <div className="horizontal-input-group">
              <label>Télécopie :</label>
              <input
                type="text"
                name="billing_fax"
                value={formData.billing_fax}
                onChange={handleChange}
              />
            </div> */}
            <div className="horizontal-input-group withrightbutton">
              <label>Mail :</label>
              <input
                type="text"
                name="billing_email"
                value={formData.email_book ? formData.email_book.split(',')[0]?.trim() : ''}
                disabled
                readOnly
              />
              <button type="button" className="rightbutton" onClick={handleShowMailbook}>
                Carnet
              </button>
            </div>
          </div>

          <div className="form-style-billing-title form-div">
            <h2>Facturation</h2>
          </div>

          <div className="form-style-billing-1 form-div">
            <div className="horizontal-input-group">
              <label>N° de SIRET :</label>
              <input
                type="text"
                name="siret_number"
                value={formData.siret_number}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>N° de TVA :</label>
              <input
                type="text"
                name="vat_number"
                value={formData.vat_number}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Code APE / NAF :</label>
              <input
                type="text"
                name="ape_naf_code"
                value={formData.ape_naf_code}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Taxe gasoil (%) :</label>
              <input
                type="number"
                name="diesel_tax"
                value={formData.diesel_tax}
                onChange={handleChange}
                onBlur={handleBlurDieselTax}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Délai d'échéance (jours) :</label>
              <input
                type="text"
                name="payment_time_from_billing"
                value={formData.payment_time_from_billing}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-billing-2 form-div">
            <div className="checkbox-input-line">
              <label>Facturation auto </label>
              <input
                type="checkbox"
                name="automatic_billing"
                checked={formData.automatic_billing}
                onChange={handleChange}
              />
            </div>
            <div className="checkbox-input-line vat">
              <label>TVA </label>
              <input
                type="checkbox"
                name="vat_status"
                checked={formData.vat_status}
                onChange={handleChange}
              />
            </div>

            <div className="multiple-radio-inputs-grid client">
              <label className="radio-group-main-label">Type de paiement :</label>
              <div className="yes-no-radio-group">
                <div>
                  <input
                    type="radio"
                    name="payment_type"
                    id="payment_type_virement"
                    value="VIREMENT"
                    checked={formData.payment_type === 'VIREMENT'}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="payment_type_virement">
                    Virement
                  </label>
                </div>
                <div>
                  <input
                    type="radio"
                    name="payment_type"
                    id="payment_type_cheque"
                    value="CHEQUE"
                    checked={formData.payment_type === 'CHEQUE'}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="payment_type_cheque">
                    Chèque
                  </label>
                </div>
              </div>
              <label className="radio-group-main-label">Période :</label>
              <div className="yes-no-radio-group">
                <div>
                  <input
                    type="radio"
                    name="period"
                    id="period_quinzaine"
                    value="QUINZAINE"
                    checked={formData.period === 'QUINZAINE'}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="period_quinzaine">
                    Quinzaine
                  </label>
                </div>
                <div>
                  <input
                    type="radio"
                    name="period"
                    id="period_mois"
                    value="MOIS"
                    checked={formData.period === 'MOIS'}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="period_mois">
                    Mois
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="form-style-endbuttons">
            <div className="endbuttons-container">
              <button
                type="button"
                onClick={handleCreateClient}
                className={!isNew ? 'hidden-preserve-space' : ''}
              >
                Créer
              </button>
              <button
                type="button"
                onClick={handleModifyClient}
                className={isNew ? 'hidden-preserve-space' : ''}
              >
                Modifier
              </button>
              <button
                type="button"
                className={`delete-button ${isNew ? 'hidden-preserve-space' : ''}`}
                onClick={handleDeleteClient}
              >
                Supprimer
              </button>
            </div>
          </div>

          <div className="form-style-prefill-title form-div">
            <span className="divider"></span>
            <div className="form-style-prefill-clickable-container" onClick={togglePrefill}>
              <h2>Pré-remplissage des informations de transport</h2>
              <div
                className={`hideButton-updown ${isPrefillHidden ? 'rotate-hide' : 'rotate-show'}`}
              >
                <button type="button"></button>
              </div>
            </div>
          </div>

          <div
            className={`form-style-vehicules-client form-div ${
              isPrefillHidden ? 'prefill-hidden' : ''
            }`}
          >
            <div className="horizontal-input-group sub-group">
              <label className="horizontal-input-group title">Vehicule</label>
              <div className="vehicules-fieldset-container">
                <div>
                  <input
                    type="checkbox"
                    name="porteur_taut"
                    id="porteur_taut"
                    checked={formData.porteur_taut}
                    onChange={handleChange}
                  />
                  <label htmlFor="porteur_taut">Porteur taut</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="semi_taut"
                    id="semi_taut"
                    checked={formData.semi_taut}
                    onChange={handleChange}
                  />
                  <label htmlFor="semi_taut">Semi taut</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="semi_fourgon"
                    id="semi_fourgon"
                    checked={formData.semi_fourgon}
                    onChange={handleChange}
                  />
                  <label htmlFor="semi_fourgon">Semi fourgon</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="porteur_fourgon"
                    id="porteur_fourgon"
                    checked={formData.porteur_fourgon}
                    onChange={handleChange}
                  />
                  <label htmlFor="porteur_fourgon">Porteur fourgon</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="semi_frigo"
                    id="semi_frigo"
                    checked={formData.semi_frigo}
                    onChange={handleChange}
                  />
                  <label htmlFor="semi_frigo">Semi frigo</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="porteur_frigo"
                    id="porteur_frigo"
                    checked={formData.porteur_frigo}
                    onChange={handleChange}
                  />
                  <label htmlFor="porteur_frigo">Porteur frigo</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="vingt_metres_cube"
                    id="vingt_metres_cube"
                    checked={formData.vingt_metres_cube}
                    onChange={handleChange}
                  />
                  <label htmlFor="vingt_metres_cube">20M3</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="vingt_metres_cube_frigo"
                    id="vingt_metres_cube_frigo"
                    checked={formData.vingt_metres_cube_frigo}
                    onChange={handleChange}
                  />
                  <label htmlFor="vingt_metres_cube_frigo">20M3 frigo</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="plateau"
                    id="plateau"
                    checked={formData.plateau}
                    onChange={handleChange}
                  />
                  <label htmlFor="plateau">Plateau</label>
                </div>
              </div>
            </div>

            <div className="multiple-radio-inputs-grid">
              <label>Hayon :</label>
              <div className="yes-no-radio-group">
                <div>
                  <input
                    type="radio"
                    name="tail_lift"
                    id="tail_lift_true"
                    value="true"
                    checked={formData.tail_lift === true}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="tail_lift_true">
                    Oui
                  </label>
                </div>
                <div>
                  <input
                    type="radio"
                    name="tail_lift"
                    id="tail_lift_false"
                    value="false"
                    checked={formData.tail_lift === false}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="tail_lift_false">
                    Non
                  </label>
                </div>
              </div>
              <label>Transpalette :</label>
              <div className="yes-no-radio-group">
                <div>
                  <input
                    type="radio"
                    name="pallet_truck"
                    id="pallet_truck_true"
                    value="true"
                    checked={formData.pallet_truck === true}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="pallet_truck_true">
                    Oui
                  </label>
                </div>
                <div>
                  <input
                    type="radio"
                    name="pallet_truck"
                    id="pallet_truck_false"
                    value="false"
                    checked={formData.pallet_truck === false}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="pallet_truck_false">
                    Non
                  </label>
                </div>
              </div>
              <label>Echange palette :</label>
              <div className="yes-no-radio-group">
                <div>
                  <input
                    type="radio"
                    name="pallet_exchanger"
                    id="pallet_exchanger_true"
                    value="true"
                    checked={formData.pallet_exchanger === true}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="pallet_exchanger_true">
                    Oui
                  </label>
                </div>
                <div>
                  <input
                    type="radio"
                    name="pallet_exchanger"
                    id="pallet_exchanger_false"
                    value="false"
                    checked={formData.pallet_exchanger === false}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="pallet_exchanger_false">
                    Non
                  </label>
                </div>
              </div>
              <label>ADR :</label>
              <div className="yes-no-radio-group">
                <div>
                  <input
                    type="radio"
                    name="adr"
                    id="adr_true"
                    value="true"
                    checked={formData.adr === true}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="adr_true">
                    Oui
                  </label>
                </div>
                <div>
                  <input
                    type="radio"
                    name="adr"
                    id="adr_false"
                    value="false"
                    checked={formData.adr === false}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="adr_false">
                    Non
                  </label>
                </div>
              </div>
            </div>

            {formData.adr === true && (
              <div className="horizontal-input-group sub-group">
                <label>Class :</label>
                <select
                  type="text"
                  name="class_type"
                  value={formData.class_type}
                  onChange={handleChange}
                >
                  {[...Array(10).keys()].map(num => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
                <label className="supp-element">Code UN :</label>
                <input
                  type="text"
                  name="un_code"
                  value={formData.un_code}
                  onChange={handleChange}
                />
                <label className="supp-element">GRP :</label>
                <select type="text" name="grp" value={formData.grp} onChange={handleChange}>
                  <option>I</option>
                  <option>II</option>
                  <option>III</option>
                </select>
              </div>
            )}
          </div>

          <div
            className={`form-style-commentaires-client form-div ${
              isPrefillHidden ? 'prefill-hidden' : ''
            }`}
          >
            <div className="vertical-input-group">
              <label>Commentaires:</label>
              <textarea name="comments" value={formData.comments} onChange={handleChange} />
            </div>
          </div>
        </form>

        {showMailBook && (
          <MailBook
            email_book={formData.email_book}
            default_emails={formData.default_emails || []}
            onClose={handleCloseMailBook}
            onConfirm={handleConfirmMailBook}
            onCancel={handleCancelMailBook}
          />
        )}

        {showMessageBox && (
          <MessageBox
            message={messageBoxConfig.message}
            onClose={() => setShowMessageBox(false)}
            onConfirm={messageBoxConfig.onConfirm}
            onCancel={messageBoxConfig.onCancel}
            type={messageBoxConfig.type}
            loading_button={messageBoxConfig.loading_button}
            input={messageBoxConfig.input}
            technicalDetails={messageBoxConfig.technicalDetails}
          />
        )}
      </div>
    </div>
  );
}
